import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import { ENTITIES, getStatusColor } from "../../services/contractService";

const DEPARTMENTS = ["HR/Admin","Finance","Operations & Asset","IT","Corp. Comms","SCM","Legal","HSE","Commercial","Engineering","Executive"];

export const ContractRequests = () => {
  const navigate = useNavigate();
  const { service, currentUser, isAdmin } = useApp();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ entity:"", department:"", year:"", categoryManager:"", status:"" });
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [updatingId, setUpdatingId] = useState(null);
  const itemsPerPage = 15;

  useEffect(() => {
    service.getListItems("Contracts").then(d => {
      let all = (d?.value || [])
        .map(i => ({...i.fields, id:i.id}))
        .sort((a, b) => new Date(b.RequestDate || 0) - new Date(a.RequestDate || 0));

      // For non-admins (category managers/users), filter to only their requests
      if (!isAdmin && currentUser?.displayName) {
        all = all.filter(c => c.CategoryManager === currentUser.displayName);
      }

      setContracts(all);
    }).catch(()=>setContracts([])).finally(()=>setLoading(false));
  }, [service, isAdmin, currentUser]);

  const sf = (k,v) => { setFilters(p=>({...p,[k]:v})); setPage(0); };

  const toggleReady = async (e, contractId, currentIsReady) => {
    e.stopPropagation();
    setUpdatingId(contractId);
    try {
      const newValue = currentIsReady === "Yes" ? "No" : "Yes";
      await service.updateListItem("Contracts", contractId, { IsReady: newValue });
      setContracts(prev => prev.map(c => c.id === contractId ? {...c, IsReady: newValue} : c));
    } catch (err) {
      console.error("Failed to update IsReady:", err);
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = contracts.filter(c => {
    const matchEntity = !filters.entity || c.Entity===filters.entity;
    const matchDept = !filters.department || c.Department===filters.department;
    const matchYear = !filters.year || c.Year===filters.year;
    const matchCategoryManager = !filters.categoryManager || c.CategoryManager===filters.categoryManager;
    const matchStatus = !filters.status || c.Status===filters.status;
    const matchSearch = !search ||
      (c.Title || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.RefNo || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.VendorName || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.ContractType || "").toLowerCase().includes(search.toLowerCase());
    return matchEntity&&matchDept&&matchYear&&matchCategoryManager&&matchStatus&&matchSearch;
  });

  const paginated = filtered.slice(page * itemsPerPage, (page + 1) * itemsPerPage);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const years = [...new Set(contracts.map(c=>c.Year).filter(Boolean))].sort((a,b)=>b-a);
  const categoryManagers = [...new Set(contracts.map(c=>c.CategoryManager).filter(Boolean))].sort();
  const statuses = [...new Set(contracts.map(c=>c.Status).filter(Boolean))].sort();

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-medium text-slate-100 m-0 mb-1">
          Contract Requests
        </h1>
        <p className="text-slate-400 m-0">All contracts across all statuses · {filtered.length} result{filtered.length!==1?"s":""}</p>
        <div className="h-0.5 bg-gradient-to-r from-yellow-600 to-transparent mt-2"/>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-5">
        {/* Search Bar */}
        <div className="mb-3.5 flex gap-2.5">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by title, reference, vendor, or type..."
            className="flex-1 px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-600"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="px-3 py-2.5 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-lg text-slate-400 text-sm font-medium"
            >
              ✕ Clear
            </button>
          )}
        </div>

        <div className={`flex gap-2.5 ${showFilters ? "mb-3.5" : ""}`}>
          <button
            onClick={()=>setShowFilters(!showFilters)}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              showFilters
                ? "bg-yellow-600/15 border border-yellow-600/20 text-yellow-600"
                : "bg-slate-800 border border-slate-700 text-slate-400 hover:text-slate-300"
            }`}
          >
            Filters
          </button>
          <button
            onClick={()=>navigate("/request")}
            className="px-4 py-2.5 bg-yellow-600 hover:bg-yellow-700 border-none rounded-lg text-slate-900 font-bold text-sm ml-auto transition-colors"
          >
            + New Request
          </button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
            <select
              value={filters.entity}
              onChange={e=>sf("entity",e.target.value)}
              className="px-2 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-600"
            >
              <option value="">All Entities</option>{ENTITIES.map(o=><option key={o}>{o}</option>)}
            </select>
            <select
              value={filters.department}
              onChange={e=>sf("department",e.target.value)}
              className="px-2 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-600"
            >
              <option value="">All Departments</option>{DEPARTMENTS.map(o=><option key={o}>{o}</option>)}
            </select>
            <select
              value={filters.year}
              onChange={e=>sf("year",e.target.value)}
              className="px-2 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-600"
            >
              <option value="">All Years</option>{years.map(y=><option key={y}>{y}</option>)}
            </select>
            <select
              value={filters.categoryManager}
              onChange={e=>sf("categoryManager",e.target.value)}
              className="px-2 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-600"
            >
              <option value="">All Category Managers</option>{categoryManagers.map(m=><option key={m}>{m}</option>)}
            </select>
            <select
              value={filters.status}
              onChange={e=>sf("status",e.target.value)}
              className="px-2 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-600"
            >
              <option value="">All Status</option>{statuses.map(s=><option key={s}>{s}</option>)}
            </select>
            <button
              onClick={()=>{setFilters({entity:"",department:"",year:"",categoryManager:"",status:""}); setPage(0);}}
              className="px-2 py-2 bg-transparent border border-slate-600 rounded-lg text-slate-400 text-sm hover:text-slate-300 transition-colors"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading requests...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-slate-700/50">
                  {["Ref. No.","Contract Title","Entity","Supplier","Department","Category Manager","Status","Ready","Requested","Cycle"].map(h=>(
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider border-b border-slate-700 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length===0 ? (
                  <tr><td colSpan={10} className="text-center py-12 text-slate-400">No pending contract requests.</td></tr>
                ) : paginated.map((c,i) => (
                  <tr
                    key={i}
                    onClick={()=>navigate("/contracts/"+c.id)}
                    className="cursor-pointer border-b border-slate-700 hover:bg-yellow-600/5 transition-colors"
                  >
                    <td className="px-4 py-3"><span className="font-mono text-sm text-yellow-600">{c.RefNo||"--"}</span></td>
                    <td className="px-4 py-3 font-medium max-w-xs truncate">{c.Title||"Untitled"}</td>
                    <td className="px-4 py-3 text-slate-400">{c.Entity}</td>
                    <td className="px-4 py-3 max-w-xs truncate">{c.VendorName}</td>
                    <td className="px-4 py-3 text-slate-400">{c.Department}</td>
                    <td className="px-4 py-3 text-slate-400 max-w-xs truncate">{c.CategoryManager||"--"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap inline-block ${
                        c.Status === "InReview" ? "bg-purple-900/30 text-purple-300" :
                        c.Status === "Pending" ? "bg-orange-900/30 text-orange-300" :
                        "bg-slate-700 text-slate-400"
                      }`}>{c.Status||"--"}</span>
                    </td>
                    {c.Status === "Requested" && (
                      <td className="px-4 py-3">
                        <button
                          onClick={e=>toggleReady(e,c.id,c.IsReady)}
                          disabled={updatingId===c.id}
                          className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-opacity ${
                            c.IsReady==="Yes"
                              ? "bg-green-900/30 text-green-300 border border-green-700"
                              : "bg-orange-900/30 text-orange-300 border border-orange-700"
                          } ${updatingId===c.id ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          {updatingId===c.id?"...":c.IsReady==="Yes"?"✅ Ready":"⚠️ Incomplete"}
                        </button>
                      </td>
                    )}
                    {c.Status !== "Requested" && <td className="px-4 py-3"></td>}
                    <td className="px-4 py-3 text-slate-400">{c.RequestDate?new Date(c.RequestDate).toLocaleDateString("en-GB"):"--"}</td>
                    <td className={`px-4 py-3 font-mono text-sm ${c.CycleDays>30?"text-red-400":"text-green-400"}`}>{c.CycleDays||"--"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-slate-700 bg-slate-700/30 text-sm text-slate-400">
            <div>Showing {page * itemsPerPage + 1} to {Math.min((page + 1) * itemsPerPage, filtered.length)} of {filtered.length}</div>
            <div className="flex gap-2">
              <button
                onClick={()=>setPage(0)}
                disabled={page===0}
                className={`px-3 py-1.5 rounded text-sm ${page===0 ? "bg-slate-700 text-slate-500 cursor-not-allowed" : "bg-slate-700 text-yellow-600 hover:text-yellow-500"}`}
              >
                First
              </button>
              <button
                onClick={()=>setPage(p=>Math.max(0,p-1))}
                disabled={page===0}
                className={`px-3 py-1.5 rounded text-sm ${page===0 ? "bg-slate-700 text-slate-500 cursor-not-allowed" : "bg-slate-700 text-yellow-600 hover:text-yellow-500"}`}
              >
                Previous
              </button>
              <span className="px-3 py-1.5 text-yellow-600 font-semibold">Page {page + 1} of {totalPages}</span>
              <button
                onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))}
                disabled={page===totalPages-1}
                className={`px-3 py-1.5 rounded text-sm ${page===totalPages-1 ? "bg-slate-700 text-slate-500 cursor-not-allowed" : "bg-slate-700 text-yellow-600 hover:text-yellow-500"}`}
              >
                Next
              </button>
              <button
                onClick={()=>setPage(totalPages-1)}
                disabled={page===totalPages-1}
                className={`px-3 py-1.5 rounded text-sm ${page===totalPages-1 ? "bg-slate-700 text-slate-500 cursor-not-allowed" : "bg-slate-700 text-yellow-600 hover:text-yellow-500"}`}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
